import React, {
  useState,
  useRef,
  useImperativeHandle,
  forwardRef,
  useCallback,
} from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  IconCamera,
  IconLibraryPhoto,
  IconFolders,
  IconCircleMinus,
  IconUpload,
  IconFileTypePdf,
  IconFileTypeDoc,
  IconFileTypePng,
  IconFileTypeJpg,
  IconFile,
} from "@tabler/icons-react";
import MDialog from "@/components/ui/atoms/MDialog";
import MDropDownDrawer, { DropdownItem } from "./MDropDownDrawer";
import FooterDrawer from "../atoms/FooterDrawer";
import { useToast } from "@/components/ui/atoms/MToastProvider";
import { useDocument } from "@/hooks/useDocument";
import { DocumentSource } from "@/types/document.dto";
import {
  triggerNativeCameraCapture,
  triggerNativeGalleryPick,
  triggerNativeFilePick,
} from "@/utils/nativeBridge";
import { handlePickerResultMessage } from "@/utils/document-utils";
import { MButton } from "../atoms/MButtons";
import MHeader from "@/components/ui/organisms/MHeader";
import { useSelector } from "react-redux";
import { RootState } from "@/store";
import MSpacer from "../atoms/MSpacer";
import { getMemberInfo } from "@/hooks/useMembers";
import { trackUserDataLoaded } from "@/utils/gtm";
import {
  ANALYTICS_SCREEN_TYPES,
  ANALYTICS_SCREEN_NAMES,
} from "@/constants/analytics.constants";
import uploadStyles from "./MDocumentUploadOptions.module.css";
import { MDocumentUploadOptionsProps } from "@/types/document.dto";

export interface MDocumentUploadOptionsHandle {
  handleDone: () => Promise<void>;
  mediaFiles: any[];
  isUploading: boolean;
}

const MDocumentUploadOptions = forwardRef<
  MDocumentUploadOptionsHandle,
  MDocumentUploadOptionsProps
>(({ open, onClose, onFilesChange }, ref) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { showToast } = useToast();
  const [isUploading, setIsUploading] = useState(false);
  const [selectedValue, setSelectedValue] = useState<string>("");
  // Track upload source for CreateDocumentDto
  const [uploadSource, setUploadSource] = useState<DocumentSource>();
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [showFileDrawer, setShowFileDrawer] = useState(false);
  const [fileToRemove, setFileToRemove] = useState<number | null>(null);
  const memberInfo = useSelector(
    (state: RootState) => state.auth?.member || {}
  );
  const currentMemberInfo = getMemberInfo();
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [showPhoneDialog, setShowPhoneDialog] = useState(false);

  // Get memberId from localStorage
  const getMemberId = (): string | undefined => {
    try {
      const memberRaw =
        localStorage.getItem("selectedMember") ||
        localStorage.getItem("member");
      if (memberRaw) {
        const member = JSON.parse(memberRaw);
        return member?.id || member?._id;
      }
    } catch (e) {
      console.error("Error getting member ID:", e);
    }
    return undefined;
  };

  const memberId = getMemberId();
  const { uploadDocument, getDocumentTypeCounts } = useDocument(memberId || "");

  // State to track if this is first upload
  const [isFirstUpload, setIsFirstUpload] = React.useState(false);

  // Check document count when modal opens - reset check each time modal opens
  React.useEffect(() => {
    const checkDocumentCount = async () => {
      if (memberId && open) {
        try {
          const counts = await getDocumentTypeCounts();
          const totalDocs = (counts as any)?.totalDocuments ?? 0;
          setIsFirstUpload(totalDocs === 0);
        } catch (error) {
          console.error("Error fetching document counts:", error);
          const fallbackValue = !currentMemberInfo?.other_attrs?.docUploaded;
          setIsFirstUpload(fallbackValue);
        }
      }
    };

    if (open) {
      checkDocumentCount();
    }
  }, [memberId, open]);

  // Handle file selection from input
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const files = Array.from(e.target.files);
      setSelectedFiles((prev) => [...prev, ...files]);
      setShowFileDrawer(true);
      // Reset the input value to allow selecting the same file again
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  // Handle selection from the dropdown
  const handleSelect = useCallback(
    (value: string) => {
      setSelectedValue(value);
      // Map string value to DocumentSource enum
      let mappedSource: DocumentSource = DocumentSource.FILES;
      if (value === "camera") mappedSource = DocumentSource.CAMERA;
      else if (value === "gallery") mappedSource = DocumentSource.GALLERY;
      else if (value === "files") mappedSource = DocumentSource.FILES;
      else if (value === "email") mappedSource = DocumentSource.EMAIL;
      else if (value === "whatsapp") mappedSource = DocumentSource.WHATSAPP;
      setUploadSource(mappedSource);

      // Track upload method selection
      if (value === "camera") {
        trackUserDataLoaded({
          memberInfo: currentMemberInfo,
          screenType: ANALYTICS_SCREEN_TYPES.DOCUMENTS,
          screenName: ANALYTICS_SCREEN_NAMES.DOCUMENTS_ADD_METHOD_PHOTO,
        });
      } else if (value === "gallery") {
        trackUserDataLoaded({
          memberInfo: currentMemberInfo,
          screenType: ANALYTICS_SCREEN_TYPES.DOCUMENTS,
          screenName: ANALYTICS_SCREEN_NAMES.DOCUMENTS_ADD_METHOD_GALLERY,
        });
      } else if (value === "files") {
        trackUserDataLoaded({
          memberInfo: currentMemberInfo,
          screenType: ANALYTICS_SCREEN_TYPES.DOCUMENTS,
          screenName: ANALYTICS_SCREEN_NAMES.DOCUMENTS_ADD_METHOD_FILES,
        });
      }

      // Check if email is registered for email upload
      if (value === "email") {
        if (!currentMemberInfo.email) {
          setShowEmailDialog(true);
          return;
        }
        localStorage.setItem("uploadSource", DocumentSource.EMAIL);
        if (typeof window !== "undefined") {
          const subject = encodeURIComponent(
            "Upload documents to Maatra (automatic processing)"
          );
          const body = encodeURIComponent(
            "Attach PDF, JPG, or PNG files up to 10MB each.\n\nFiles sent here are secure, confidential, and processed automatically. No one from our team reads or views them.\n\nYou'll receive a confirmation in the app once they're processed."
          );
          window.location.href = `mailto:documents@maatra.health?subject=${subject}&body=${body}`;
        }
        return;
      }

      if (value === "whatsapp") {
        if (!currentMemberInfo.mobile) {
          setShowPhoneDialog(true);
          return;
        }
        localStorage.setItem("uploadSource", DocumentSource.WHATSAPP);

        if (typeof window !== "undefined") {
          const waNumber = "919870597100";

          // Native app deep link
          const appUrl = `whatsapp://send?phone=${waNumber}`;

          // Fallback for Desktop Web
          const webUrl = `https://wa.me/${waNumber}`;

          // Try opening native app first
          window.location.href = appUrl;

          // Fallback to WhatsApp web if not opened
          setTimeout(() => {
            window.location.href = webUrl;
          }, 500);
        }
        return;
      }

      if (typeof window !== "undefined" && (window as any).ReactNativeWebView) {
        // Native app - use native bridge for camera/gallery/files
        switch (value) {
          case "camera":
            localStorage.setItem("uploadSource", DocumentSource.CAMERA);
            triggerNativeCameraCapture();
            break;
          case "gallery":
            localStorage.setItem("uploadSource", DocumentSource.GALLERY);
            triggerNativeGalleryPick();
            break;
          case "files":
            localStorage.setItem("uploadSource", DocumentSource.FILES);
            triggerNativeFilePick();
            break;
        }
      } else {
        // Web - use file input for camera/gallery/files
        const input = document.createElement("input");
        input.type = "file";
        input.multiple = true;
        input.accept = "image/*,application/pdf";

        if (value === "camera") {
          input.capture = "environment";
        }
        input.onchange = (e) => {
          const files = (e.target as HTMLInputElement).files;
          if (files && files.length > 0) {
            const newFiles = Array.from(files);
            // Immediately navigate to the upload review page
            navigate("/documents/upload-review", {
              state: {
                files: newFiles,
                memberId,
                memberInfo,
                backUrl: window.location.pathname,
                uploadSource: mappedSource,
                isFirstUpload,
              },
            });
          }
        };
        input.click();
      }
    },
    [currentMemberInfo.email, currentMemberInfo.mobile]
  );

  // Handle file removal
  const handleRemoveFile = (index: number) => {
    setFileToRemove(index);
  };

  const confirmRemoveFile = () => {
    if (fileToRemove === null) return;

    const newFiles = [...selectedFiles];
    newFiles.splice(fileToRemove, 1);
    setSelectedFiles(newFiles);

    if (onFilesChange) {
      onFilesChange(newFiles);
    }

    if (newFiles.length === 0) {
      setShowFileDrawer(false);
    }

    setFileToRemove(null);
  };

  const cancelRemoveFile = () => {
    setFileToRemove(null);
  };

  // Handle upload of all selected files
  const handleUpload = async () => {
    if (selectedFiles.length === 0) return;
    setIsUploading(true);
    try {
      if (!memberId) {
        // showToast({ message: 'Member ID is missing', variant: 'error' });
        setIsUploading(false);
        return;
      }

      const duplicateNames: string[] = [];
      let uploadedCount = 0;
      let failedCount = 0;

      for (const file of selectedFiles) {
        try {
          await uploadDocument(file, uploadSource || DocumentSource.APP);
          uploadedCount++;
        } catch (e: any) {
          if (e?.response?.status === 409) {
            duplicateNames.push(
              e?.response?.data?.message?.fileName || "Unknown"
            );
          } else {
            failedCount++;
          }
        }
      }

      setSelectedFiles([]);
      setShowFileDrawer(false);
      onClose();
      if (onFilesChange) onFilesChange([]);

      if (uploadedCount > 0) {
        showToast({
          message: "Files uploaded successfully",
          variant: "success",
        });
      }
      if (duplicateNames.length > 0) {
        showToast({
          message: `${
            duplicateNames.length
          } file(s) already uploaded: ${duplicateNames.join(", ")}`,
          variant: "error",
        });
      }
      if (failedCount > 0) {
        showToast({
          message: `${failedCount} file(s) failed to upload. Please try again.`,
          variant: "error",
        });
      }

      navigate("/documents");
    } catch (error) {
      console.error("Error uploading files:", error);
      showToast({ message: "Failed to upload files", variant: "error" });
    } finally {
      setIsUploading(false);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    setSelectedFiles([]);
    setShowFileDrawer(false);
    onClose();
  };

  // Listen for native picker results
  React.useEffect(() => {
    async function handleNativeMessage(event: MessageEvent) {
      try {
        const files = await handlePickerResultMessage(event);
        if (files && files.length > 0) {
          setSelectedFiles((prev) => [...prev, ...files]);
          //setShowFileDrawer(true);
          navigate("/documents/upload-review", {
            state: {
              files: files,
              memberId,
              memberInfo,
              backUrl: window.location.pathname,
              uploadSource: localStorage.getItem("uploadSource"),
              isFirstUpload,
            },
          });
        }
      } catch (error) {
        console.error("Error handling native message:", error);
        showToast({
          message: "Failed to process selected files",
          variant: "error",
        });
      }
    }

    window.addEventListener("message", handleNativeMessage);
    return () => window.removeEventListener("message", handleNativeMessage);
  }, [showToast]);

  // Expose methods via ref
  useImperativeHandle(ref, () => ({
    handleDone: async () => {
      return Promise.resolve();
    },
    mediaFiles: [],
    isUploading,
  }));

  const options: DropdownItem[] = [
    {
      value: "camera",
      label: t("document.uploadOptions.camera", "Take Photo"),
      icon: <IconCamera size={20} />,
    },
    {
      value: "gallery",
      label: t("document.uploadOptions.gallery", "Select from gallery"),
      icon: <IconLibraryPhoto size={20} />,
    },
    {
      value: "files",
      label: (
        <div className="flex flex-row items-center gap-1">
          <span>{t("document.uploadOptions.files", "Select from files")}</span>
          <span className={uploadStyles.uploadSubtitle}>
            (up to 10 per upload)
          </span>
        </div>
      ),
      icon: <IconFolders size={20} />,
    },
    {
      value: "email",
      label: t("document.uploadOptions.email", "By Email"),
      icon: <IconFolders size={20} />,
    },
    {
      value: "whatsapp",
      label: t("document.uploadOptions.whatsapp", "By Whatsapp"),
      icon: <IconFolders size={20} />,
    },
  ];

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        multiple
        className="hidden"
        onChange={handleFileChange}
        accept="image/*,application/pdf"
      />

      {/* Main Dropdown Drawer for Upload Options */}
      <MDropDownDrawer
        key={open ? "open" : "closed"}
        open={open}
        onClose={onClose}
        title={t("document.uploadOptions.title", "Select upload option")}
        items={options}
        onSelect={handleSelect}
        selectedValues={selectedValue ? [selectedValue] : []}
        height="45vh"
        textVariant="md"
      />

      {/* Footer Drawer for Selected Files */}
      <FooterDrawer
        open={showFileDrawer}
        onClose={() => setShowFileDrawer(false)}
        doneText="Upload"
        doneDisabled={isUploading || selectedFiles.length === 0}
        height="100vh"
      >
        <div
          style={{ padding: "24px", marginBottom: "-53px", marginTop: "-5px" }}
        >
          <MHeader
            module=""
            memberInfo={memberInfo}
            title={`Upload ${selectedFiles.length} File${
              selectedFiles.length !== 1 ? "s" : ""
            }`}
            description="Review your selected files before uploading."
            learnMoreContent="Uploaded files become part of your secure health profile. From this screen, you can check file names, remove unwanted ones, or start uploading."
            drawerTitle="More Info"
          />
        </div>
        <MSpacer mt={20} />
        <div className="flex flex-col gap-4 p-6">
          {/* Total upload size */}
          <div className="text-xs font-semibold text-gray-600">
            Total upload size:{" "}
            {(
              selectedFiles.reduce((total, file) => total + file.size, 0) /
              (1024 * 1024)
            ).toFixed(2)}{" "}
            MB
          </div>

          <div
            className="overflow-y-auto flex flex-col gap-[12px]"
            style={{ maxHeight: "calc(85vh - 280px)", minHeight: "100px" }}
          >
            {selectedFiles.map((file, index) => {
              // Get file extension and type
              const fileExt = file.name.split(".").pop()?.toLowerCase() || "";
              const isPdf = fileExt === "pdf";
              const isDoc = ["doc", "docx", "odt"].includes(fileExt);

              // Format file size
              const fileSize =
                file.size > 1024 * 1024
                  ? `${(file.size / (1024 * 1024)).toFixed(2)} MB`
                  : `${(file.size / 1024).toFixed(1)} KB`;

              return (
                <>
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 hover:bg-gray-50 rounded-lg border border-gray-100"
                  >
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <div className="flex-shrink-0 w-9 h-9 flex items-center justify-center bg-gray-100 rounded color-[#3263A3]">
                        {isPdf ? (
                          <IconFileTypePdf size={24} />
                        ) : isDoc ? (
                          <IconFileTypeDoc size={24} />
                        ) : fileExt === "png" ? (
                          <IconFileTypePng size={24} />
                        ) : fileExt === "jpg" || fileExt === "jpeg" ? (
                          <IconFileTypeJpg size={24} />
                        ) : (
                          <IconFile size={24} />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="body-base-semibold default-text-color truncate">
                          {file.name}
                        </p>
                        <p className="text-xs font-medium text-gray-500 mt-0.5">
                          {fileSize}
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveFile(index);
                      }}
                      className="text-[#983145] hover:opacity-80 p-1.5 rounded-full hover:bg-gray-100 transition-opacity flex-shrink-0"
                      disabled={isUploading}
                      aria-label="Remove file"
                    >
                      <IconCircleMinus size={20} />
                    </button>

                    <MDialog
                      isOpen={fileToRemove === index}
                      onClose={cancelRemoveFile}
                      title="Remove File"
                      description="Are you sure you want to remove this file? This action cannot be undone."
                      primaryCtaText="Remove"
                      onPrimaryCta={confirmRemoveFile}
                      secondaryCtaText="Cancel"
                      onSecondaryCta={cancelRemoveFile}
                      destructivePrimary={true}
                    />
                  </div>
                </>
              );
            })}
          </div>

          <div className="confirm-footer">
            <MButton
              variant="outline"
              size="md"
              onClick={handleCancel}
              disabled={isUploading}
              className="flex-1 w-[48%]"
            >
              Cancel
            </MButton>
            <MButton
              variant="filled"
              onClick={handleUpload}
              size="md"
              leftIcon={!isUploading ? <IconUpload size={16} /> : undefined}
              className="flex-1 w-[48%]"
              disabled={isUploading}
            >
              {isUploading ? "Uploading..." : "Upload"}
            </MButton>
          </div>
        </div>
      </FooterDrawer>

      {/* Email Not Registered Dialog */}
      <MDialog
        isOpen={showEmailDialog}
        onClose={() => setShowEmailDialog(false)}
        title="Email not registered"
        description="Register your email on Maatra to use this upload option."
        primaryCtaText="Register now"
        onPrimaryCta={() => {
          setShowEmailDialog(false);
          navigate(`/profile/basic-information/contact?open=acc1`);
        }}
        secondaryCtaText="Cancel"
        onSecondaryCta={() => setShowEmailDialog(false)}
      />

      {/* Phone Number Not Registered Dialog */}
      <MDialog
        isOpen={showPhoneDialog}
        onClose={() => setShowPhoneDialog(false)}
        title="Phone number not registered"
        description="Register your phone number on Maatra to use this upload option."
        primaryCtaText="Register now"
        onPrimaryCta={() => {
          setShowPhoneDialog(false);
          navigate(`/profile/basic-information/contact?open=acc1`);
        }}
        secondaryCtaText="Cancel"
        onSecondaryCta={() => setShowPhoneDialog(false)}
      />
    </>
  );
});

MDocumentUploadOptions.displayName = "MDocumentUploadOptions";

export { MDocumentUploadOptions };
export default MDocumentUploadOptions;
